import { useCallback, useEffect, useRef, useState } from "react";

// Debounced, coalescing autosave hook.
//
// - `data`   the current snapshot to persist (must be replaced with a NEW reference on every
//            edit — the callers of this hook use immutable update handlers, so this holds).
// - `saveFn` (snapshot) => Promise — posts one save for the given snapshot.
// - options.delay    debounce window in ms (default 1200).
// - options.enabled  when false, no saves are scheduled (e.g. while a column has no program yet).
//
// Behavior:
// - The FIRST non-null `data` this hook observes is treated as a freshly-loaded baseline, not an
//   edit, and does NOT trigger a save.
// - Every subsequent change to `data` (re)starts the debounce timer. A ref always tracks the
//   latest snapshot, so several edits inside the debounce window collapse into a single save of
//   the final state ("coalescing").
// - If the debounce timer fires while a save is already in-flight, the fire is queued and re-run
//   the instant the in-flight request settles — this guarantees at most one in-flight SaveProgram
//   call at a time without ever silently dropping a newer edit.
// - On success the hook only flips its own `status` to "saved" — callers must NOT re-fetch/re-load
//   `data` after a successful save (that would disturb this column's own edit/scroll state and,
//   for a multi-column screen, could look like it disturbed sibling state too).
// - `reset()` re-arms the "skip the first emission" behavior. Callers should invoke it
//   synchronously, before swapping in a freshly loaded snapshot for a *different* underlying
//   record (e.g. the user picked a different Phase/Period/Workout for this column), so the newly
//   loaded snapshot is treated as a new baseline instead of an "edit" that would autosave garbage
//   instantly.
// - Any pending debounce timer is cancelled on unmount.
export default function useAutosave(data, saveFn, options = {}) {
  const { delay = 1200, enabled = true, savedDisplayMs = 2000 } = options;

  const [status, setStatus] = useState("idle");

  const latestDataRef = useRef(data);
  const timerRef = useRef(null);
  const savedTimerRef = useRef(null);
  const savingRef = useRef(false);
  const pendingRef = useRef(false);
  const isFirstDataRef = useRef(true);
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const runSave = useCallback(() => {
    if (savingRef.current) {
      pendingRef.current = true;
      return;
    }
    savingRef.current = true;
    // A new save is starting — cancel any pending "hide the Saved chip" timer.
    if (savedTimerRef.current) {
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = null;
    }
    setStatus("saving");
    Promise.resolve()
      .then(() => saveFnRef.current(latestDataRef.current))
      .then(() => {
        setStatus("saved");
        // Show "Saved" only briefly once the request finishes, then clear back to idle (no chip)
        // until the next change starts another save.
        savedTimerRef.current = setTimeout(() => {
          savedTimerRef.current = null;
          setStatus("idle");
        }, savedDisplayMs);
      })
      .catch((err) => {
        setStatus("error");
        return err;
      })
      .finally(() => {
        savingRef.current = false;
        if (pendingRef.current) {
          pendingRef.current = false;
          runSave();
        }
      });
  }, []);

  useEffect(() => {
    latestDataRef.current = data;

    if (!enabled || data == null) {
      return undefined;
    }

    if (isFirstDataRef.current) {
      // This is the just-loaded baseline, not an edit — establish it and bail.
      isFirstDataRef.current = false;
      return undefined;
    }

    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      runSave();
    }, delay);

    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enabled, delay]);

  // Cancel any pending timers on unmount (e.g. the column was removed).
  useEffect(() => {
    return () => {
      clearTimer();
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
        savedTimerRef.current = null;
      }
    };
  }, [clearTimer]);

  const reset = useCallback(() => {
    clearTimer();
    if (savedTimerRef.current) {
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = null;
    }
    isFirstDataRef.current = true;
    pendingRef.current = false;
    setStatus("idle");
  }, [clearTimer]);

  return { status, reset };
}
